import { useEffect, useSyncExternalStore } from 'react'

export enum SideNavigationEvent {
  Collapse = 'collapse',
  Expand = 'expand',
}

class SideNavigationState {
  private _subscribers: Set<() => void> = new Set()

  constructor(
    private _status: 'wide' | 'narrow',
    private _lock = false,
    private _capacity: number = Infinity,
    private _navigationHeight = 0,
  ) {}

  subscribe = (callback: () => void) => {
    this._subscribers.add(callback)
    return () => {
      this._subscribers.delete(callback)
    }
  }

  updateSubscribers = () => {
    this._subscribers.forEach((callback) => callback())
  }

  getStatus = () => this._status

  getLock = () => this._lock

  getCapacity = () => this._capacity

  getNavigationHeight = () => this._navigationHeight

  setStatus = (status: 'wide' | 'narrow') => {
    if (this._status !== status) {
      this._status = status
      this.updateSubscribers()
    }
  }

  toggleStatus = () => {
    this._status = this._status === 'wide' ? 'narrow' : 'wide'
    this.updateSubscribers()
  }

  setLock = (lock: boolean) => {
    if (this._lock !== lock) {
      this._lock = lock
      this.updateSubscribers()
    }
  }

  setCapacity = (capacity: number) => {
    if (this._capacity !== capacity) {
      this._capacity = capacity
      this.updateSubscribers()
    }
  }

  setNavigationHeight = (height: number) => {
    if (this._navigationHeight !== height) {
      this._navigationHeight = height
      this.updateSubscribers()
    }
  }
}

const sideNavigationState = new SideNavigationState('narrow', false)

export function useSideNavigationGlobalStateInit({
  initialStatus,
  onUpdateStatus,
}: {
  initialStatus: 'wide' | 'narrow'
  onUpdateStatus: (status: 'wide' | 'narrow') => void
}) {
  useEffect(() => {
    return sideNavigationState.subscribe(() => {
      onUpdateStatus?.(sideNavigationState.getStatus())
    })
  }, [onUpdateStatus])

  useEffect(() => {
    sideNavigationState.setStatus(initialStatus)
  }, [initialStatus])

  useEffect(() => {
    const handleCollapse = () => {
      sideNavigationState.setStatus('narrow')
    }
    const handleExpand = () => {
      sideNavigationState.setStatus('wide')
    }
    window.addEventListener(SideNavigationEvent.Collapse, handleCollapse)
    window.addEventListener(SideNavigationEvent.Expand, handleExpand)
    return () => {
      window.removeEventListener(SideNavigationEvent.Collapse, handleCollapse)
      window.removeEventListener(SideNavigationEvent.Expand, handleExpand)
    }
  }, [])
}

export function useSideNavigationGlobalState() {
  const status = useSyncExternalStore<'wide' | 'narrow'>(sideNavigationState.subscribe, () =>
    sideNavigationState.getStatus(),
  )
  const lock = useSyncExternalStore<boolean>(sideNavigationState.subscribe, () =>
    sideNavigationState.getLock(),
  )
  const capacity = useSyncExternalStore<number>(sideNavigationState.subscribe, () =>
    sideNavigationState.getCapacity(),
  )
  const navigationHeight = useSyncExternalStore<number>(sideNavigationState.subscribe, () =>
    sideNavigationState.getNavigationHeight(),
  )

  return {
    status,
    lock,
    capacity,
    navigationHeight,
    setStatus: sideNavigationState.setStatus,
    toggleStatus: sideNavigationState.toggleStatus,
    setLock: sideNavigationState.setLock,
    setCapacity: sideNavigationState.setCapacity,
    setNavigationHeight: sideNavigationState.setNavigationHeight,
  }
}
